"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/redux/store";
import {
  closeModal,
  setLoggedIn as setReduxLoggedIn,
} from "@/redux/features/LoginModal/loginModalSlice";
import Icon from "../icons/Icons";
import {
  validatePhone,
  validateEmail,
  validateName,
  validatePassword,
} from "@/utils/validation";
import {
  serverCheckPhoneExists,
  serverCheckEmailExists,
  serverAuthOTPSend,
  serverAuthOTPVerify,
  serverLoginUser,
  serverRegisterUser,
  serverRegisterGoogleUser,
  serverForgotPassword,
} from "@/utils/serverApiFile";
import { handleGoogleSignIn } from "@/utils/authService";
import CustomModal from "./CustomModal";
import FloatingInput from "../ui/FloatingInputField";
import SixDigitOtpInput from "../ui/SixDigitOtpInput";
import Lottie from "lottie-react";
import successAnimation from "./../../../public/next_assets/lottie/success-tick.json";

declare global {
  interface Window {
    gapi: {
      load: (api: string, callback: () => void) => void;
      auth2: {
        init: (config: { client_id: string; cookiepolicy: string }) => {
          signIn: () => Promise<GoogleUser>;
        };
      };
    };
    _googleUserId: string;
  }
}

interface GoogleUser {
  getBasicProfile: () => {
    getEmail: () => string;
    getId: () => string;
    getName?: () => string;
  };
}

type ModalStep =
  | "identifier"
  | "signin_otp"
  | "signin_password"
  | "signup_details"
  | "signup_otp"
  | "google_phone"
  | "google_otp"
  | "forgot_password"
  | "forgot_password_success"
  | "success";

const getDelay = (count: number) => {
  if (count === 0) return 30_000;
  if (count === 1) return 3 * 60_000;
  return 5 * 60_000;
};

const LoginModal = ({ pageKey }: { pageKey?: string | null | undefined }) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [step, setStep] = useState<ModalStep>("identifier");
  const [loading, setLoading] = useState(false);
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);

  // Form values
  const [phoneOrEmail, setPhoneOrEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [inputType, setInputType] = useState<"phone" | "email" | null>(null);

  // OTP and verification states
  const [otp, setOtp] = useState("");
  const [otpOrderNo, setOtpOrderNo] = useState<string | number>("");
  const [otpSuccessMessage, setOtpSuccessMessage] = useState("");
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const resendTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Consent & Messages
  const [whatsappConsent, setWhatsappConsent] = useState(true);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordSuccessMessage, setForgotPasswordSuccessMessage] =
    useState("");
  const [successMessage, setSuccessMessage] = useState("Login successful.");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Google Temp Data
  const [googleName, setGoogleName] = useState("");
  const [googleEmail, setGoogleEmail] = useState("");
  const [googleId, setGoogleId] = useState("");

  const pathname = usePathname();
  const dismissCount = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();
  const { isOpen, modalKey } = useSelector(
    (state: RootState) => state.loginModal,
  );
  const isLoggedIn = useSelector(
    (state: RootState) => state.loginModal.isLoggedIn,
  );

  const setLoggedIn = (value: boolean) => dispatch(setReduxLoggedIn(value));
  const close = () => dispatch(closeModal());

  // Countdown timer for OTP resend
  const startResendTimer = useCallback(() => {
    setCanResend(false);
    setResendTimer(30);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (resendTimerRef.current) clearInterval(resendTimerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  const resetFields = useCallback(() => {
    setStep("identifier");
    setPhoneOrEmail("");
    setPhone("");
    setName("");
    setEmail("");
    setPassword("");
    setOtp("");
    setOtpOrderNo("");
    setInputType(null);
    setForgotPasswordEmail("");
    setForgotPasswordSuccessMessage("");
    setOtpSuccessMessage("");
    setSuccessMessage("Login successful.");
    setErrors({});
    setCanResend(false);
    setResendTimer(30);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
  }, []);

  const scheduleOpen = useCallback(() => {
    if (isLoggedIn) return;
    const delay = getDelay(dismissCount.current);
    timerRef.current = setTimeout(() => setOpenModal(true), delay);
  }, [isLoggedIn]);

  const handleClose = useCallback(() => {
    setOpenModal(false);
    close();
    resetFields();
    dismissCount.current += 1;
    scheduleOpen();
  }, [close, resetFields, scheduleOpen]);

  useEffect(() => {
    if (isLoggedIn) return;
    scheduleOpen();
    return () => clearTimeout(timerRef.current ?? undefined);
  }, [isLoggedIn, scheduleOpen]);

  useEffect(() => {
    setOpenModal(isOpen ?? false);
  }, [isOpen]);

  // Trap focus inside modal
  useEffect(() => {
    if (!isOpen) return;
    const modal = modalRef.current;
    if (!modal) return;

    const focusable = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    modal.addEventListener("keydown", handleKeyDown);
    return () => modal.removeEventListener("keydown", handleKeyDown);
  }, [step, isOpen]);

  // Handle Input Changes
  const handleInputChange = (field: string, rawValue: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      delete newErrors.general;
      return newErrors;
    });

    if (field === "phone") {
      const digits = rawValue.replace(/\D/g, "").slice(0, 10);
      setPhone(digits);
    } else if (field === "phoneOrEmail") {
      setPhoneOrEmail(rawValue);
    } else if (field === "name") {
      const formatted = rawValue
        .replace(/\s{2,}/g, " ")
        .split(" ")
        .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : ""))
        .join(" ");
      setName(formatted);
    } else if (field === "email") {
      setEmail(rawValue.toLowerCase().trim());
    } else if (field === "password") {
      setPassword(rawValue);
    } else if (field === "forgotPasswordEmail") {
      setForgotPasswordEmail(rawValue.toLowerCase().trim());
    }
  };

  const handleOtpChange = (val: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.otp;
      delete newErrors.general;
      return newErrors;
    });
    setOtpSuccessMessage("");
    const digits = val.replace(/\D/g, "").slice(0, 6);
    setOtp(digits);
  };

  // Google Sign-in
  const handleGoogleBtn = async () => {
    const clientId =
      "245951504565-ialuhbrtiq7o4q8mqjskn4ij39jp8blg.apps.googleusercontent.com";
    setLoading(true);
    setErrors({});

    try {
      const checkData = await handleGoogleSignIn(clientId);
      if (checkData?.existOrNot?.status === 1) {
        setSuccessMessage("Login successful.");
        setStep("success");

        setTimeout(() => {
          setLoggedIn(true);
          handleClose();
        }, 2000);
      } else {
        setGoogleName(checkData.name || "");
        setGoogleEmail(checkData.email || "");
        setGoogleId(checkData.google_id || "");
        setPhone("");
        setOtp("");
        setStep("google_phone");
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Google login failed. Please try again.";
      setErrors({ phoneOrEmail: message });
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (!canResend || loading || isOtpVerifying) return;
    setErrors({});
    setLoading(true);
    try {
      const cleanPhone = phone.replace(/\D/g, "").slice(-10);
      const res = await serverAuthOTPSend({ mobile: cleanPhone });
      if (Number(res?.status) === 1) {
        if (res.orderNo) {
          setOtpOrderNo(res.orderNo);
        }
        setOtpSuccessMessage(
          res.message || `New OTP sent to +91 ${cleanPhone}`,
        );
        startResendTimer();
      } else {
        setErrors({ otp: res.message || "Failed to resend OTP" });
      }
    } catch (err) {
      console.error(err, "LoginModal: Error ResendOtp");
      setErrors({ otp: "Failed to resend OTP. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Identifier Continue
  const handleIdentifierSubmit = async () => {
    const input = phoneOrEmail.trim();
    if (!input) {
      setErrors({ phoneOrEmail: "Please enter phone no. or email" });
      return;
    }

    const cleanDigits = input.replace(/\D/g, "");
    const isPhoneNumber =
      cleanDigits.length === 10 ||
      (cleanDigits.length > 10 && cleanDigits.startsWith("91"));

    if (isPhoneNumber) {
      const targetPhone = cleanDigits.slice(-10);
      if (!validatePhone(targetPhone)) {
        setErrors({ phoneOrEmail: "Enter a valid 10-digit mobile number" });
        return;
      }

      setLoading(true);
      try {
        const exists = await serverCheckPhoneExists(targetPhone);
        setInputType("phone");
        setPhone(targetPhone);

        if (exists) {
          const otpRes = await serverAuthOTPSend({ mobile: targetPhone });
          if (Number(otpRes?.status) === 1) {
            if (otpRes.orderNo) setOtpOrderNo(otpRes.orderNo);
            setOtpSuccessMessage(
              otpRes.message || `OTP sent to +91 ${targetPhone}`,
            );
            startResendTimer();
          } else {
            setCanResend(true);
            setErrors({
              otp: otpRes.message || "Unable to send OTP. Please try again.",
            });
          }
          setOtp("");
          setStep("signin_otp");
        } else {
          setStep("signup_details");
        }
      } catch (err) {
        console.error(err, "LoginModal: Error IdentifierSubmit (phone)");
        setErrors({
          phoneOrEmail: "Unable to verify phone number. Try again.",
        });
      } finally {
        setLoading(false);
      }
    } else {
      if (!validateEmail(input)) {
        setErrors({
          phoneOrEmail: "Enter a valid email address or 10-digit mobile number",
        });
        return;
      }

      setLoading(true);
      try {
        const targetEmail = input.toLowerCase().trim();
        const exists = await serverCheckEmailExists(targetEmail);
        setInputType("email");
        setEmail(targetEmail);

        if (exists) {
          setStep("signin_password");
        } else {
          setStep("signup_details");
        }
      } catch (err) {
        console.error(err, "LoginModal: Error IdentifierSubmit (email)");
        setErrors({ phoneOrEmail: "Unable to verify email. Try again." });
      } finally {
        setLoading(false);
      }
    }
  };

  // Step 2A: Phone OTP Verify
  const handleSignInOtpSubmit = async (customOtp?: string) => {
    const targetOtp = customOtp || otp;
    if (!targetOtp || targetOtp.length < 6) {
      setErrors({ otp: "Please enter a valid 6-digit OTP" });
      return;
    }

    setLoading(true);
    setIsOtpVerifying(true);
    try {
      const cleanPhone = phone.replace(/\D/g, "").slice(-10);
      const verifyRes = await serverAuthOTPVerify({
        otp: targetOtp,
        mobile: cleanPhone,
        orderNo: otpOrderNo,
      });

      if (Number(verifyRes?.status) === 1) {
        setSuccessMessage("Login successful.");
        setStep("success");
        setTimeout(() => {
          setLoggedIn(true);
          handleClose();
        }, 2000);
      } else {
        setErrors({
          otp: verifyRes.message || "Invalid OTP. Please try again.",
        });
      }
    } catch (err) {
      console.error(err, "LoginModal: Error SignInOtpSubmit");
      setErrors({ otp: "OTP verification failed. Please try again." });
    } finally {
      setLoading(false);
      setIsOtpVerifying(false);
    }
  };

  // Step 2B: Email Password Login
  const handleSignInPasswordSubmit = async () => {
    if (!password.trim()) {
      setErrors({ password: "Enter your password to continue" });
      return;
    }

    setLoading(true);
    try {
      const result = await serverLoginUser(email, "", password);
      if (Number(result?.status) === 1) {
        setSuccessMessage("Login successful.");
        setStep("success");
        setTimeout(() => {
          setLoggedIn(true);
          handleClose();
        }, 2000);
      } else {
        setErrors({
          password:
            result.msg || result.message || "Invalid credentials. Try again.",
        });
      }
    } catch (err) {
      console.error(err, "LoginModal: Error SignInPasswordSubmit");
      setErrors({ password: "Login failed. Please check your credentials." });
    } finally {
      setLoading(false);
    }
  };

  // Step 3A: Signup Details Submit -> Send Verification OTP to Mobile
  const handleSignUpDetailsSubmit = async () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim() || !validateName(name)) {
      newErrors.name = "Enter a valid full name";
    }

    if (inputType !== "email") {
      if (!validateEmail(email)) newErrors.email = "Enter a valid email";
    }

    const cleanPhone = phone.replace(/\D/g, "").slice(-10);
    if (inputType !== "phone") {
      if (!validatePhone(cleanPhone))
        newErrors.phone = "Enter a valid 10-digit mobile number";
    }

    if (!validatePassword(password)) {
      newErrors.password = "Min 8 chars, 1 capital, 1 number";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      if (inputType !== "email") {
        const emailExists = await serverCheckEmailExists(email);
        if (emailExists) {
          setErrors({ email: "This email is already registered." });
          setLoading(false);
          return;
        }
      }

      if (inputType !== "phone") {
        const phoneExists = await serverCheckPhoneExists(cleanPhone);
        if (phoneExists) {
          setErrors({ phone: "This phone number is already registered." });
          setLoading(false);
          return;
        }
      }

      // Always send verification OTP to mobile number
      const otpRes = await serverAuthOTPSend({ mobile: cleanPhone });
      if (Number(otpRes?.status) === 1) {
        if (otpRes.orderNo) setOtpOrderNo(otpRes.orderNo);
        setOtp("");
        setOtpSuccessMessage(otpRes.message || `OTP sent to +91 ${cleanPhone}`);
        startResendTimer();
        setStep("signup_otp");
      } else {
        const errorMsg = otpRes.message || "Failed to send verification OTP";
        setErrors({
          general: errorMsg,
          ...(inputType === "phone" ? {} : { phone: errorMsg }),
        });
      }
    } catch (err) {
      console.error(err, "LoginModal: Error SignUpDetailsSubmit");
      setErrors({
        general: "Verification failed. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Step 3B: Signup OTP Verify & Register
  const handleSignUpOtpSubmit = async (customOtp?: string) => {
    const targetOtp = customOtp || otp;
    if (!targetOtp || targetOtp.length < 6) {
      setErrors({ otp: "Please enter a valid 6-digit OTP" });
      return;
    }

    setLoading(true);
    setIsOtpVerifying(true);
    try {
      const cleanPhone = phone.replace(/\D/g, "").slice(-10);
      const verifyRes = await serverAuthOTPVerify({
        otp: targetOtp,
        mobile: cleanPhone,
        orderNo: otpOrderNo,
      });

      if (Number(verifyRes?.status) === 1) {
        const regRes = await serverRegisterUser(
          name,
          email,
          cleanPhone,
          password,
          pathname,
          modalKey,
        );

        if (Number(regRes?.status) === 1) {
          setSuccessMessage("Account created successfully!");
          setStep("success");
          setTimeout(() => {
            setLoggedIn(true);
            handleClose();
          }, 2000);
        } else {
          setErrors({
            otp: regRes.msg || regRes.message || "Registration failed.",
          });
        }
      } else {
        setErrors({
          otp: verifyRes.message || "Invalid OTP. Please try again.",
        });
      }
    } catch (err) {
      console.error(err, "LoginModal: Error SignUpOtpSubmit");
      setErrors({ otp: "Registration failed. Please try again." });
    } finally {
      setLoading(false);
      setIsOtpVerifying(false);
    }
  };

  // Step 4A: Google Phone Submit -> Send Verification OTP
  const handleGooglePhoneSubmit = async () => {
    const cleanPhone = phone.replace(/\D/g, "").slice(-10);
    if (!cleanPhone || !validatePhone(cleanPhone)) {
      setErrors({ phone: "Enter a valid 10-digit mobile number" });
      return;
    }

    setLoading(true);
    try {
      const exists = await serverCheckPhoneExists(cleanPhone);
      if (exists) {
        setErrors({
          phone: "Number already exists. Please login or try another number.",
        });
        setLoading(false);
        return;
      }

      const otpRes = await serverAuthOTPSend({ mobile: cleanPhone });
      if (Number(otpRes?.status) === 1) {
        if (otpRes.orderNo) setOtpOrderNo(otpRes.orderNo);
        setOtp("");
        setOtpSuccessMessage(otpRes.message || `OTP sent to +91 ${cleanPhone}`);
        startResendTimer();
        setStep("google_otp");
      } else {
        setErrors({
          phone: otpRes.message || "Failed to send OTP. Please try again.",
        });
      }
    } catch (err) {
      console.error(err, "LoginModal: Error GooglePhoneSubmit");
      setErrors({ phone: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Step 4B: Google OTP Verify & Complete
  const handleGoogleOtpSubmit = async (customOtp?: string) => {
    const targetOtp = customOtp || otp;
    if (!targetOtp || targetOtp.length < 6) {
      setErrors({ otp: "Please enter a valid 6-digit OTP" });
      return;
    }

    setLoading(true);
    setIsOtpVerifying(true);
    try {
      const cleanPhone = phone.replace(/\D/g, "").slice(-10);
      const verifyRes = await serverAuthOTPVerify({
        otp: targetOtp,
        mobile: cleanPhone,
        orderNo: otpOrderNo,
      });

      if (Number(verifyRes?.status) === 1) {
        const regRes = await serverRegisterGoogleUser({
          name: googleName,
          email: googleEmail,
          mobile: cleanPhone,
          google_id: googleId,
          leadSource: pathname,
          button_type: modalKey || "login_modal",
          whatsapp_status: 1,
        });

        if (Number(regRes?.status) === 1) {
          setSuccessMessage("Account created successfully!");
          setStep("success");
          setTimeout(() => {
            setLoggedIn(true);
            handleClose();
          }, 2000);
        } else {
          setErrors({
            otp: regRes.msg || regRes.message || "Registration failed",
          });
        }
      } else {
        setErrors({
          otp: verifyRes.message || "Invalid OTP. Please try again.",
        });
      }
    } catch (err) {
      console.error(err, "LoginModal: Error GoogleOtpSubmit");
      setErrors({ otp: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
      setIsOtpVerifying(false);
    }
  };

  // Step 6: Forgot Password Submit
  const handleForgotPasswordSubmit = async () => {
    const targetEmail = forgotPasswordEmail.trim();
    if (!targetEmail || !validateEmail(targetEmail)) {
      setErrors({ forgotPasswordEmail: "Enter a valid email address" });
      return;
    }

    setLoading(true);
    try {
      const result = await serverForgotPassword(targetEmail);
      if (result && result.status === 1) {
        setForgotPasswordSuccessMessage(
          result.message ||
            "We have sent you a password reset link on your email",
        );
        setStep("forgot_password_success");
      } else {
        setErrors({
          forgotPasswordEmail:
            result?.message || "Failed to send reset link. Please try again.",
        });
      }
    } catch (err) {
      console.error(err, "LoginModal: Error ForgotPasswordSubmit");
      setErrors({
        forgotPasswordEmail: "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Unified CTA Continue Click Handler
  const handleContinue = async () => {
    if (loading || isOtpVerifying) return;

    switch (step) {
      case "identifier":
        return handleIdentifierSubmit();
      case "signin_otp":
        return handleSignInOtpSubmit();
      case "signin_password":
        return handleSignInPasswordSubmit();
      case "signup_details":
        return handleSignUpDetailsSubmit();
      case "signup_otp":
        return handleSignUpOtpSubmit();
      case "google_phone":
        return handleGooglePhoneSubmit();
      case "google_otp":
        return handleGoogleOtpSubmit();
      case "forgot_password":
        return handleForgotPasswordSubmit();
      case "forgot_password_success":
        setErrors({});
        setPassword("");
        setInputType("email");
        setEmail(forgotPasswordEmail);
        setStep("signin_password");
        break;
      default:
        break;
    }
  };

  const handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const activeElement = document.activeElement as HTMLElement;
      if (!activeElement || activeElement.tagName !== "BUTTON") {
        handleContinue();
      } else {
        activeElement.click();
      }
    }
  };

  const handleBack = () => {
    setErrors({});
    setOtp("");
    setOtpSuccessMessage("");
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);

    if (step === "signin_otp" || step === "signin_password") {
      setStep("identifier");
    } else if (step === "signup_details") {
      setStep("identifier");
    } else if (step === "signup_otp") {
      setStep("signup_details");
    } else if (step === "google_phone") {
      setStep("identifier");
    } else if (step === "google_otp") {
      setStep("google_phone");
    } else if (step === "forgot_password") {
      if (inputType === "email") {
        setStep("signin_password");
      } else {
        setStep("identifier");
      }
    } else if (step === "forgot_password_success") {
      setStep("forgot_password");
    } else {
      setStep("identifier");
    }
  };

  const getSubmitButtonText = () => {
    switch (step) {
      case "identifier":
        return "Continue";
      case "signin_otp":
        return "Verify & Sign In";
      case "signin_password":
        return "Sign In";
      case "signup_details":
        return "Get Started";
      case "signup_otp":
        return "Verify & Create Account";
      case "google_phone":
        return "Continue";
      case "google_otp":
        return "Verify & Complete";
      case "forgot_password":
        return "Send Reset Link";
      case "forgot_password_success":
        return "Return to Login";
      default:
        return "Continue";
    }
  };

  let imageKey = "";
  let loginHeading = "Join Us for Exclusive Access!";

  if (pageKey === "scheme-detail") {
    imageKey = "-scheme";
    loginHeading = "Get Exclusive Insights for this Fund";
  } else if (pageKey === "tools-and-calculators") {
    imageKey = "-calc";
    loginHeading = "Turn Your Calculation into Action";
  } else if (pageKey === "digital-gold") {
    imageKey = "-digital-gold";
    loginHeading = "Join Us & Unlock Your Gold Edge";
  } else if (pageKey === "overlap") {
    imageKey = "-overlap";
  } else if (pageKey === "compare-funds") {
    imageKey = "-compare";
  } else if (pageKey === "sif") {
    imageKey = "-sif";
  } else if (pageKey === "portfolio-services") {
    imageKey = "-portfolio";
  } else if (pageKey === "assessment") {
    imageKey = "-assessment";
  }

  if (isLoggedIn || pageKey === "login-page" || pageKey === "homePage")
    return null;

  return (
    <>
      <CustomModal
        isOpen={openModal}
        isBlurOverlay={true}
        mobileCondition={631}
        closeOnOverlayClick={false}
        onClose={handleClose}
        maxSize="xs:max-w-162.5 sm:max-w-[715px] min-h-85"
        className="flex flex-col xs:flex-row rounded-t-2xl xs:rounded-2xl bg-white shadow-xl z-999999 overflow-hidden"
      >
        {/* Left Side: Dynamic Image */}
        <div className="relative w-full xs:w-72.75 h-46.25 xs:h-auto xs:min-h-85 rounded-2xl overflow-hidden shrink-0 bg-white p-1.5 flex flex-col justify-center">
          <Image
            src={`/next_assets/images/login-popup/login-desktop${imageKey}.png`}
            alt="Fund Recommendation"
            width={300}
            height={336}
            className="hidden xs:block object-fill object-center w-full h-full rounded-2xl"
            priority
          />
          <Image
            src={`/next_assets/images/login-popup/login-mobile${imageKey}.png`}
            alt="Popup Image"
            width={300}
            height={336}
            className="xs:hidden object-fill object-center w-full h-full rounded-2xl"
            priority
          />
        </div>

        {/* Right Side: Form Card */}
        <div
          ref={modalRef}
          className="w-full flex flex-col justify-center py-5 px-3 sm:px-8 relative min-w-70 max-h-[85vh] xs:max-h-115 overflow-y-auto scrollbar-none loginModal"
        >
          {step !== "success" && (
            <div className="flex items-center justify-start gap-2 mb-3.5">
              {step !== "identifier" && (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={loading || isOtpVerifying}
                  className="text-sm text-(--text) hover:opacity-75 transition cursor-pointer shrink-0"
                  aria-label="Back"
                >
                  <Icon.loginBackBtn />
                </button>
              )}

              <p className="text-(--text) font-semibold text-lg sm:text-xl line-clamp-1">
                {step === "identifier" && loginHeading}
                {step === "signin_otp" && "Verify Number"}
                {step === "signin_password" && "Welcome Back!"}
                {step === "signup_details" && "Create Your Account"}
                {step === "signup_otp" && "Verify Number"}
                {step === "google_phone" && "Enter Your Phone Number"}
                {step === "google_otp" && "Verify Number"}
                {step === "forgot_password" && "Forgot Password?"}
                {step === "forgot_password_success" && "Check Your Email"}
              </p>
            </div>
          )}

          {errors.general && (
            <div className="p-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-medium mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 shrink-0 text-red-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{errors.general}</span>
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* STEP 1: Phone or Email Identifier */}
            {step === "identifier" && (
              <motion.div
                key="step-identifier"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                <FloatingInput
                  type="text"
                  value={phoneOrEmail}
                  loading={loading}
                  isDisabled={loading}
                  onChange={(v) => handleInputChange("phoneOrEmail", v)}
                  onKeyDown={handleEnterKey}
                  label="Enter Phone or Email"
                  error={errors.phoneOrEmail}
                />
              </motion.div>
            )}

            {/* STEP 2A: Phone Login OTP Verification */}
            {step === "signin_otp" && (
              <motion.div
                key="step-signin-otp"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="space-y-3 mb-5"
              >
                <SixDigitOtpInput
                  label="Enter 6-Digit OTP"
                  value={otp}
                  isDisabled={loading || isOtpVerifying}
                  onChange={handleOtpChange}
                  error={errors.otp}
                  onComplete={(code) => handleSignInOtpSubmit(code)}
                />

                {otpSuccessMessage && !errors.otp && (
                  <p className="text-2xs text-emerald-600 font-medium flex items-center gap-1">
                    <svg
                      className="w-3.5 h-3.5 inline shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {otpSuccessMessage}
                  </p>
                )}

                <div className="flex justify-between items-center text-xs px-1">
                  <span className="text-(--text) opacity-70">
                    Didn&apos;t receive OTP?
                  </span>
                  {canResend ? (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={loading || isOtpVerifying}
                      className="font-semibold text-(--primary) hover:underline cursor-pointer disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  ) : (
                    <span className="text-(--text) opacity-50 font-medium">
                      Resend in{" "}
                      <span className="font-semibold text-(--text) opacity-80">
                        {resendTimer}s
                      </span>
                    </span>
                  )}
                </div>
              </motion.div>
            )}

            {/* STEP 2B: Email Login with Password */}
            {step === "signin_password" && (
              <motion.div
                key="step-signin-password"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordEmail(email);
                    setErrors({});
                    setStep("forgot_password");
                  }}
                  className="block w-fit ml-auto text-sm line-clamp-1.1 text-(--primary) font-semibold text-right mb-1 sm:hover:underline cursor-pointer"
                >
                  Forgot Password?
                </button>

                <FloatingInput
                  type="password"
                  value={password}
                  isDisabled={loading}
                  onChange={(v) => handleInputChange("password", v)}
                  onKeyDown={handleEnterKey}
                  label="Enter Password"
                  error={errors.password}
                />
              </motion.div>
            )}

            {/* STEP 3A: Create Account Details */}
            {step === "signup_details" && (
              <motion.div
                key="step-signup-details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                <FloatingInput
                  type="text"
                  value={name}
                  isDisabled={loading}
                  onChange={(v) => handleInputChange("name", v)}
                  onKeyDown={handleEnterKey}
                  label="Enter Full Name"
                  error={errors.name}
                />

                {inputType !== "phone" && (
                  <FloatingInput
                    type="tel"
                    value={phone}
                    isDisabled={loading}
                    onChange={(v) => handleInputChange("phone", v)}
                    onKeyDown={handleEnterKey}
                    label="Enter Phone Number"
                    error={errors.phone}
                  />
                )}

                {inputType !== "email" && (
                  <FloatingInput
                    type="email"
                    value={email}
                    isDisabled={loading}
                    onChange={(v) => handleInputChange("email", v)}
                    onKeyDown={handleEnterKey}
                    label="Enter Email"
                    error={errors.email}
                  />
                )}

                <FloatingInput
                  type="password"
                  value={password}
                  isDisabled={loading}
                  onChange={(v) => handleInputChange("password", v)}
                  onKeyDown={handleEnterKey}
                  label="Create Password"
                  error={errors.password}
                />
              </motion.div>
            )}

            {/* STEP 3B: Registration Mobile OTP Verification */}
            {step === "signup_otp" && (
              <motion.div
                key="step-signup-otp"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="space-y-3 mb-3"
              >
                <SixDigitOtpInput
                  label="Enter 6-Digit OTP"
                  value={otp}
                  isDisabled={loading || isOtpVerifying}
                  onChange={handleOtpChange}
                  error={errors.otp}
                  onComplete={(code) => handleSignUpOtpSubmit(code)}
                />

                {otpSuccessMessage && !errors.otp && (
                  <p className="text-2xs text-emerald-600 font-medium flex items-center gap-1">
                    <svg
                      className="w-3.5 h-3.5 inline shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {otpSuccessMessage}
                  </p>
                )}

                <div className="flex justify-between items-center text-xs px-1">
                  <span className="text-(--text) opacity-70">
                    Didn&apos;t receive OTP?
                  </span>
                  {canResend ? (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={loading || isOtpVerifying}
                      className="font-semibold text-(--primary) hover:underline cursor-pointer disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  ) : (
                    <span className="text-(--text) opacity-50 font-medium">
                      Resend in{" "}
                      <span className="font-semibold text-(--text) opacity-80">
                        {resendTimer}s
                      </span>
                    </span>
                  )}
                </div>
              </motion.div>
            )}

            {/* STEP 4A: Google Phone Entry */}
            {step === "google_phone" && (
              <motion.div
                key="step-google-phone"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                <div className="p-2.5 rounded-xl bg-[rgb(var(--primary-alpha)/0.08)] border border-[rgb(var(--primary-alpha)/0.2)] flex items-center gap-2.5 mb-3">
                  <Icon.googleIcon className="w-5 h-5 shrink-0" />
                  <div className="text-xs text-(--text) leading-tight">
                    <span className="font-semibold block">{googleName}</span>
                    <span className="text-(--primary)">{googleEmail}</span>
                  </div>
                </div>

                <FloatingInput
                  type="tel"
                  value={phone}
                  isDisabled={loading}
                  onChange={(v) => handleInputChange("phone", v)}
                  onKeyDown={handleEnterKey}
                  label="Enter Phone Number"
                  error={errors.phone}
                />
              </motion.div>
            )}

            {/* STEP 4B: Google OTP Verification */}
            {step === "google_otp" && (
              <motion.div
                key="step-google-otp"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
                className="space-y-3"
              >
                <div className="flex items-center justify-between text-xs text-(--text) opacity-80 px-1">
                  <span>Verification code sent to:</span>
                  <span className="font-semibold text-(--primary)">
                    +91 {phone}
                  </span>
                </div>

                <SixDigitOtpInput
                  label="Enter 6-Digit OTP"
                  value={otp}
                  isDisabled={loading || isOtpVerifying}
                  onChange={handleOtpChange}
                  error={errors.otp}
                  onComplete={(code) => handleGoogleOtpSubmit(code)}
                />

                {otpSuccessMessage && !errors.otp && (
                  <p className="text-2xs text-emerald-600 font-medium flex items-center gap-1">
                    <svg
                      className="w-3.5 h-3.5 inline shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {otpSuccessMessage}
                  </p>
                )}

                <div className="flex justify-between items-center text-xs px-1">
                  <span className="text-(--text) opacity-70">
                    Didn&apos;t receive OTP?
                  </span>
                  {canResend ? (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={loading || isOtpVerifying}
                      className="font-semibold text-(--primary) hover:underline cursor-pointer disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  ) : (
                    <span className="text-(--text) opacity-50 font-medium">
                      Resend in{" "}
                      <span className="font-semibold text-(--text) opacity-80">
                        {resendTimer}s
                      </span>
                    </span>
                  )}
                </div>
              </motion.div>
            )}

            {/* STEP 5: Success Screen */}
            {step === "success" && (
              <motion.div
                key="step-success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className="py-4"
              >
                <div className="relative space-y-2 text-center">
                  <Lottie
                    className="w-28 h-28 mx-auto"
                    animationData={successAnimation}
                    loop={false}
                    autoplay={true}
                  />
                  <p className="text-base sm:text-lg font-semibold text-(--text)">
                    {successMessage}
                  </p>
                </div>
              </motion.div>
            )}

            {/* STEP 6: Forgot Password Form */}
            {step === "forgot_password" && (
              <motion.div
                key="step-forgot-password"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.25 }}
              >
                <FloatingInput
                  type="email"
                  value={forgotPasswordEmail}
                  loading={loading}
                  isDisabled={loading}
                  onChange={(v) => handleInputChange("forgotPasswordEmail", v)}
                  onKeyDown={handleEnterKey}
                  label="Enter Registered Email"
                  error={errors.forgotPasswordEmail}
                />
              </motion.div>
            )}

            {/* STEP 6B: Forgot Password Confirmation */}
            {step === "forgot_password_success" && (
              <motion.div
                key="step-forgot-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="py-2 text-center space-y-3"
              >
                <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shadow-xs">
                  <Icon.emailIconV4 className="w-6 h-6" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm sm:text-base sm:leading-6 font-semibold text-(--text) leading-snug">
                    {forgotPasswordSuccessMessage ||
                      "We have sent you a password reset link on your email"}
                  </p>

                  <p className="text-xs text-(--text) opacity-60 pt-1 leading-relaxed max-w-xs mx-auto">
                    Please check your inbox or spam folder and click the link to
                    reset your password.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action CTA Button */}
          {step !== "success" && (
            <button
              onClick={handleContinue}
              disabled={loading || isOtpVerifying}
              className="w-full border border-(--secondary) shrink-0 bg-(--secondary) text-white font-medium py-2.5 rounded-xl transition shadow-[0px_3.46px_13.83px_0px_#0c3e9f2e] hover:opacity-95 disabled:opacity-60 cursor-pointer"
            >
              {loading || isOtpVerifying ? (
                <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                getSubmitButtonText()
              )}
            </button>
          )}

          {/* Step 1 Social Login and Legal Disclaimer */}
          {step === "identifier" && (
            <>
              <div className="relative my-2.5 text-center">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <span className="relative bg-white px-2 text-xs text-gray-500 font-medium">
                  OR
                </span>
              </div>

              <button
                type="button"
                onClick={handleGoogleBtn}
                disabled={loading}
                className="w-full border border-gray-300 py-2.5 text-[#344054] font-medium rounded-xl flex items-center justify-center gap-2 transition duration-300 hover:bg-gray-50 disabled:opacity-60 cursor-pointer"
              >
                <Icon.googleIcon />
                Continue with Google
              </button>
            </>
          )}
        </div>
      </CustomModal>
    </>
  );
};

export default LoginModal;
